// apps/merchant-v3/src/lib/scope/ScopeVersionProvider.tsx
//
// Phase 6.1 Wave 5 Task 23 — the P6.1.D-21 scopeVersion interceptor. The BE
// resolveStoreScope middleware (task-19) stamps an `x-scope-version` header
// on every scoped read. The api/client forwards the parsed value here via
// `setScopeVersionObserver`. When the version increases versus the last one
// we saw, the member's store access changed under them (assignment edit /
// team deactivate) — so we:
//   1. invalidate the whole TanStack Query cache (scope-dependent reads
//      refetch with fresh scope), and
//   2. show a non-blocking toast "Store access changed — refreshing"
//      (t('teams:scopeChanged')).
// This converges stale FE caches without a dedicated /me/scope poll and
// without a WebSocket (polling-by-ride-along, D-21/D-28). The first version
// observed only seeds the baseline (no toast).
//
// Mounted once (inside QueryClientProvider + Toaster + I18nProvider) around
// the authenticated shell.
//
// vg-binding: PLAN:task-23
// vg-binding: P6.1.D-21
// vg-binding: P6.1.D-26
// vg-binding: P6.1.D-19
// vg-binding: P6.1.D-04
// vg-binding: INTERFACE-STANDARDS:error-shape

import { useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@printway/ui';
import { useTranslation } from 'react-i18next';

import { setScopeVersionObserver } from '@/lib/api/client';

export interface ScopeVersionProviderProps {
  children: ReactNode;
}

export function ScopeVersionProvider({
  children,
}: ScopeVersionProviderProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('teams');
  // Last version seen. null = not yet seeded (first header only sets baseline).
  const lastVersion = useRef<number | null>(null);
  // Coalesce a burst of scoped reads carrying the same new version into one
  // refresh (multiple queries land near-simultaneously on a drift).
  const refreshing = useRef(false);

  useEffect(() => {
    setScopeVersionObserver((version: number) => {
      if (lastVersion.current === null) {
        lastVersion.current = version;
        return;
      }
      if (version <= lastVersion.current) return; // monotonic — ignore stale/equal
      lastVersion.current = version;
      if (refreshing.current) return;
      refreshing.current = true;
      // Non-blocking toast + full cache invalidation so every scope-dependent
      // surface refetches with the caller's fresh scope (D-21).
      toast({ title: t('scopeChanged'), variant: 'warning' });
      void queryClient
        .invalidateQueries()
        .finally(() => {
          refreshing.current = false;
        });
    });
    return () => setScopeVersionObserver(null);
  }, [queryClient, toast, t]);

  return <>{children}</>;
}

export default ScopeVersionProvider;
